import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getRequest } from '../../../../../config';
import { setTempModule, clearTempModule } from '../../../../../reducers/filesAllocationReducer';

/**
 * Fetches a temp module by its slug (or name) including all sections,
 * questions, and existing answers for the given company.
 *
 * Uses GET /modules/temp-modules/by-name/:moduleSlug?companyId=xxx
 *
 * The response is shaped exactly like company.modules[tab] so it can be
 * dropped directly into QuestionAnswersPage without any UI changes.
 */
export function useModuleData(companyId, moduleSlug, company) {
    const dispatch = useDispatch();
    const [state, setState] = useState({
        module: null,
        sections: [],
        loading: true,
        error: null,
    });

    // Parse directors-questionnaire_${directorId} tab format
    let resolvedModuleName = moduleSlug;
    let resolvedDirectorId = null;
    if (moduleSlug.includes('directors-questionnaire_')) {
        const parts = moduleSlug.split('_');
        resolvedModuleName = 'directors-questionnaire';
        resolvedDirectorId = parts[1] || null;
    } else if (moduleSlug.includes('business-audit')) {
        resolvedModuleName = moduleSlug.replace(`-${company?.['testType']}`, '');
    } else if (moduleSlug.includes('%20')) {
        resolvedModuleName = moduleSlug.replaceAll('%20', ' ');
    }
    const moduleName = resolvedModuleName;
    const directorId = resolvedDirectorId;

    const fetchData = useCallback(async () => {
        if (!moduleName || !companyId || (typeof companyId === "object" && Object.keys(companyId).length === 0)) return;
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const isValidCompanyId = typeof companyId === 'string' && companyId.trim().length > 0;
            let query = isValidCompanyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
            if (directorId) query += `${query ? '&' : '?'}directorId=${encodeURIComponent(directorId)}`;
            const res = await getRequest(`/modules/temp-modules/by-name/${encodeURIComponent(moduleName)}${query}`);

            if (!res || res.code !== 200) {
                throw new Error(res?.message || 'Failed to load module data.');
            }
            const module = res?.body;
            if (!module) throw new Error(`Module "${moduleName}" not found.`);

            setState({
                module,
                sections: module.sections || [],
                loading: false,
                error: null,
            });

            // Cache for consumers that depend on the active temp module (e.g. OpenRModalComponentNew).
            dispatch(setTempModule({
                ...module,
                moduleSlug: module.moduleSlug || moduleSlug,
                moduleName: module.moduleName || moduleName,
                companyId,
            }));
        } catch (err) {
            setState(prev => ({ ...prev, loading: false, error: err.message }));
        }
    }, [companyId, moduleName, moduleSlug, directorId, dispatch]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        return () => {
            dispatch(clearTempModule());
        };
    }, [dispatch]);

    return { ...state, refetch: fetchData };
}
